import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';

const SupabaseAuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};

export const SupabaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (mounted) {
          if (error) throw error;
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (e) {
        if (mounted) {
          console.error('Error getting session:', e.message);
          setError(e.message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle different auth events
        switch (event) {
          case 'SIGNED_IN':
            // Remove hash from URL if it exists (from OAuth or magic link)
            if (window.location.hash) {
              window.location.hash = '';
            }
            // Only navigate to dashboard if we're not already there
            if (!location.pathname.includes('/dashboard')) {
              navigate('/dashboard');
            }
            break;
          case 'SIGNED_OUT':
            navigate('/');
            break;
          case 'USER_UPDATED':
            setUser(session?.user ?? null);
            break;
          case 'USER_DELETED':
            setUser(null);
            setSession(null);
            navigate('/');
            break;
          default:
            break;
        }
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [navigate, location.pathname]);

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setSession(null);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error.message);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async ({ email, password, options = {} }) => {
    try {
      setLoading(true);
      setError(null);
      
      const redirectTo = process.env.NODE_ENV === 'production'
        ? 'https://clicdroits.info/auth/callback'
        : `${window.location.origin}/auth/callback`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          ...options,
          emailRedirectTo: redirectTo,
          data: {
            ...options.data,
            created_at: new Date().toISOString(),
          },
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error signing up:', error.message);
      setError(error.message);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signInWithPassword = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error signing in:', error.message);
      setError(error.message);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signInWithOAuth = async (provider) => {
    try {
      setLoading(true);
      setError(null);

      const redirectTo = process.env.NODE_ENV === 'production'
        ? 'https://clicdroits.info/auth/callback'
        : `${window.location.origin}/auth/callback`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error signing in with OAuth:', error.message);
      setError(error.message);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signInWithOtp = async (email) => {
    try {
      setLoading(true);
      setError(null);

      const redirectTo = process.env.NODE_ENV === 'production'
        ? 'https://clicdroits.info/auth/callback'
        : `${window.location.origin}/auth/callback`;

      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo,
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error signing in with OTP:', error.message);
      setError(error.message);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    session,
    user,
    loading,
    error,
    signOut,
    signUp,
    signInWithPassword,
    signInWithOAuth,
    signInWithOtp,
  };

  return (
    <SupabaseAuthContext.Provider value={value}>
      {loading && !session ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        children
      )}
    </SupabaseAuthContext.Provider>
  );
}; 